import { useState , useEffect } from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { Button } from '@amzn/ring-react-components/csr/components/Button';
import useStores from '$src/hooks/useStores';
import useFeature from '$src/hooks/useFeature';
import Locations from './Locations';
import useRedirectOnSingleOrNoLocation from './useRedirectOnSingleOrNoLocation';
import isLocationActive from './isLocationActive';
import SearchLocation from './SearchLocation';
import Layout from '../Layout';

import './index.less';

function LocationsSelector({ header, handleClick, ...props }) {
  const [activeLocation, setActiveLocation] = useState({});
  const [isVisibleLocationSetupComplete, setVisibleShowLocationSetupComplete] = useState(true);
  const [isVisibleLocationSetupIncomplete, setVisibleShowLocationSetupIncomplete] = useState(true);
  const { sessionStore, locationStore } = useStores();
  const { isMobile } = sessionStore;
  const [isLocationSelectorSmbCustomersEnabled] = useFeature('stark-feature-next-location-selector-smb-customers')
  const { locations, onboardedLocations, unboardedLocations } = locationStore;
  const [unboardedLocationsFiltered, setUnboardedLocationsFiltered] = useState(unboardedLocations);
  const [onboardedLocationsFiltered, setOnboardedLocationsFiltered] = useState(onboardedLocations);
  const noLocations =
    (unboardedLocationsFiltered.length === 0 && onboardedLocationsFiltered.length === 0) ||
    (unboardedLocationsFiltered.length === 0 && !isVisibleLocationSetupComplete) ||
    (onboardedLocationsFiltered.length === 0 && !isVisibleLocationSetupIncomplete) ||
    (!isVisibleLocationSetupIncomplete && !isVisibleLocationSetupComplete);

  useEffect(() => {
    setUnboardedLocationsFiltered(unboardedLocations);
    setOnboardedLocationsFiltered(onboardedLocations);
  }, [onboardedLocations, unboardedLocations]);

  useRedirectOnSingleOrNoLocation({ locations, unboardedLocations, onboardedLocations, handleClick, isMobile });

  const onApplyClearFilter = () => {
    setActiveLocation({});
    setVisibleShowLocationSetupComplete(true);
    setVisibleShowLocationSetupIncomplete(true);
  };

  const onApplySetupCompleteFilter = () => {
    setActiveLocation({});
    setVisibleShowLocationSetupComplete(true);
    setVisibleShowLocationSetupIncomplete(false);
  };

  const onApplySetupIncompleteFilter = () => {
    setActiveLocation({});
    setVisibleShowLocationSetupComplete(false);
    setVisibleShowLocationSetupIncomplete(true);
  };

  const onChangeSearchLocations = (value) => {
    setActiveLocation({});

    setUnboardedLocationsFiltered(
      unboardedLocations.filter((location) => location.locationName.toLowerCase().includes(value.target.value.toLowerCase())),
    );

    setOnboardedLocationsFiltered(
      onboardedLocations.filter((location) => location.locationName.toLowerCase().includes(value.target.value.toLowerCase())),
    );
  };

  return (
    <Layout
      hideBack
      subheader={
        isLocationSelectorSmbCustomersEnabled && (
          <SearchLocation
            onChangeSearchLocations={onChangeSearchLocations}
            onApplySetupIncompleteFilter={onApplySetupIncompleteFilter}
            onApplySetupCompleteFilter={onApplySetupCompleteFilter}
            onApplyClearFilter={onApplyClearFilter}
          />
        )
      }
    >
      <div className="locations-selector" data-testid={props['data-testid']}>
        {header}
        <>
          <div className="locations-selector__container">
            {unboardedLocationsFiltered.length > 0 && isVisibleLocationSetupIncomplete && (
              <Locations
                locations={unboardedLocationsFiltered || unboardedLocations}
                activeLocation={activeLocation}
                setActiveLocation={setActiveLocation}
                unboarded
                title="Setup Incomplete"
              />
            )}
            {onboardedLocationsFiltered.length > 0 && isVisibleLocationSetupComplete && (
              <Locations
                locations={onboardedLocationsFiltered}
                activeLocation={activeLocation}
                setActiveLocation={setActiveLocation}
                title="Setup Complete"
              />
            )}
            {noLocations && (
              <p className="locations-selector__container--empty" data-testid="location-selector-no-locations-found">
                No locations found.
              </p>
            )}
          </div>
          <div className="locations-selector-footer">
            <Button
              disabled={!activeLocation.locationId || !isLocationActive(activeLocation)}
              className="locations-selector-footer-btn--continue"
              data-testid="locations-selector-footer-button"
              color="primary"
              size="m"
              variant="filled"
              onClick={() => handleClick(activeLocation)}
            >
              Continue
            </Button>
          </div>
        </>
      </div>
    </Layout>
  );
}

LocationsSelector.propTypes = {
  handleClick: PropTypes.func.isRequired,
  header: PropTypes.node,
  'data-testid': PropTypes.string,
};

export default observer(LocationsSelector);
